import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import { decrementModalCount } from "@/redux/meta/metaActions";
import GoVipSpin2WinModal from "@/components/modals/GoVipSpin2WinModal";
import { getDateInEst } from "@/utils/getDateInEst";
import styles from "./ActionCards.module.scss";

interface SpinTheWheelCardProps {
    onSpin?: () => void;
    isAvailable?: boolean;
    onModalOpen?: () => void;
    onModalClose?: () => void;
}

const SpinTheWheelCard: React.FC<SpinTheWheelCardProps> = ({ onSpin, isAvailable, onModalOpen, onModalClose }) => {
    const [showSpinModal, setShowSpinModal] = useState(false);
    const [spinAvailable, setSpinAvailable] = useState(false);
    const currentUser = useSelector((store: IStore) => store.metaStore.currentUser);
    const dispatch = useDispatch();

    // Check if spin is available based on lastSpin date
    useEffect(() => {
        if (currentUser?.user) {
            const lastSpinDate = currentUser.user.lastSpin
                ? getDateInEst(new Date(currentUser.user.lastSpin))
                : null;

            const currentDate = new Date().toLocaleString("en-US", { timeZone: "America/New_York" });
            const startOfToday = new Date(currentDate);
            startOfToday.setHours(0, 0, 0, 0);

            const canSpin = !lastSpinDate || lastSpinDate < startOfToday;
            setSpinAvailable(canSpin);
        }
    }, [currentUser?.user?.lastSpin]);

    const toggleSpinModal = () => {
        const wasOpen = showSpinModal;
        setShowSpinModal(!showSpinModal);

        // Only run cleanup when modal was actually open and is now closing
        if (wasOpen) {
            dispatch(decrementModalCount());
            // After closing modal, trigger the onSpin callback to remove card from rotation
            if (onSpin) onSpin();
            if (onModalClose) onModalClose();
        }
    };

    const handleOpenSpin = () => {
        setShowSpinModal(true);
        if (onModalOpen) onModalOpen();
    };

    // Don't render if spin not available, BUT keep rendering if modal is currently open
    if ((!spinAvailable || isAvailable === false) && !showSpinModal) {
        return null;
    }

    return (
        <>
            <div
                className="stat-card"
                onClick={handleOpenSpin}
                style={{
                    cursor: "pointer",
                    backgroundImage: "url(/images/card-bck6.png)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    border: "1px solid rgba(147, 51, 234, 0.3)",
                    boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 4px 12px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(147, 51, 234, 0.15)",
                    position: "relative",
                }}
            >
                <div style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "rgba(0, 0, 0, 0.4)",
                    borderRadius: "inherit",
                }}></div>
                <div style={{ position: "relative", zIndex: 1 }}>
                    <h4 style={{ color: "white", textShadow: "0 2px 4px rgba(0, 0, 0, 0.9)", fontWeight: "700", letterSpacing: "0.5px" }}>🎡 DAILY SPIN</h4>
                    <div className="stat-value" style={{ color: "#9F7AEA", textShadow: "0 2px 4px rgba(0, 0, 0, 0.9)", fontWeight: "700", fontSize: "1.1rem" }}>
                        {spinAvailable ? "WIN XP!" : "TOMORROW"}
                    </div>
                    <div className="stat-subtitle" style={{ color: "rgba(255, 255, 255, 0.95)", textShadow: "0 1px 2px rgba(0, 0, 0, 0.9)", fontWeight: "500" }}>
                        {spinAvailable ? "Your daily spin awaits" : "Come back tomorrow"}
                    </div>
                </div>
            </div>

            {/* GoVip Spin Modal */}
            <GoVipSpin2WinModal
                isOpen={showSpinModal}
                toggle={toggleSpinModal}
            />
        </>
    );
};

export default SpinTheWheelCard;